import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { Certification, certificationsApi, CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN, type IssueCredentialPayload, type UpdateCertificationPayload } from "@/lib/api";
import { calculateProgress, getCertificateStatusVariant, getCertificateStatusBadgeClass } from "@/utils/certification-utils";
import { toast } from "sonner";

interface ProcessCertificationModalProps {
  certification: Certification | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

type CredentialType = 'certificate' | 'badge' | 'letter';
type Action = 'APPROVE' | 'REJECT' | 'SET_PENDING' | 'ISSUE';

function isPlaceholderProgramName(name?: string | null): boolean {
  if (!name) return true;
  const t = name.trim();
  return t.length === 0 || t.toUpperCase() === "PENDING";
}

export const ProcessCertificationModal: React.FC<ProcessCertificationModalProps> = ({
  certification,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [selectedCredentials, setSelectedCredentials] = React.useState<Set<CredentialType>>(new Set());
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [sendEmail, setSendEmail] = React.useState(false);
  const [profileIssueOverride, setProfileIssueOverride] = React.useState(false);
  const [profileIssueOverrideReason, setProfileIssueOverrideReason] = React.useState("");
  
  // Requirement states
  const [theoryStatus, setTheoryStatus] = React.useState<string>('');
  const [clinicalStatus, setClinicalStatus] = React.useState<string>('');
  const [paymentStatus, setPaymentStatus] = React.useState<string>('');
  const [documentationStatus, setDocumentationStatus] = React.useState<string>('');
  const [graduationStatus, setGraduationStatus] = React.useState<string>('');
  const [programName, setProgramName] = React.useState('');
  
  // Credential status states
  const [certificateStatus, setCertificateStatus] = React.useState<string>('');
  const [badgeStatus, setBadgeStatus] = React.useState<string>('');
  const [letterStatus, setLetterStatus] = React.useState<string>('');

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open && certification) {
      setSelectedCredentials(new Set());
      setRejectionReason('');
      setSendEmail(false);
      setProfileIssueOverride(false);
      setProfileIssueOverrideReason("");
      
      // Initialize requirement states with current values
      setTheoryStatus(certification.theory?.key || 'PENDING');
      setClinicalStatus(certification.clinical?.key || 'NOT_REQUIRED');
      setPaymentStatus(certification.payment?.key || 'PENDING');
      setDocumentationStatus(certification.documentation?.key || 'PENDING');
      setGraduationStatus(certification.graduation?.key || 'PENDING');
      setProgramName(
        isPlaceholderProgramName(certification.programName)
          ? ""
          : (certification.programName || "").trim()
      );
      
      // Initialize credential status states
      setCertificateStatus(certification.certificate?.status || 'PENDING');
      setBadgeStatus(certification.digitalBadge?.status || 'PENDING');
      setLetterStatus(certification.letter?.status || 'PENDING');
    }
  }, [open, certification]);

  if (!certification) return null;

  const progress = calculateProgress(certification);
  const enrolledProgram = certification.enrolledProgram?.trim() || "";
  const initialStoredProgram = isPlaceholderProgramName(certification.programName)
    ? ""
    : (certification.programName || "").trim();
  const programNameChanged = programName.trim() !== initialStoredProgram;
  const printedProgramPreview =
    programName.trim() || enrolledProgram || "—";

  const toggleCredential = (type: CredentialType) => {
    setSelectedCredentials(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const getCredentialStatus = (type: CredentialType): string => {
    switch (type) {
      case 'certificate':
        return certification.certificate?.status || 'PENDING';
      case 'badge':
        return certification.digitalBadge?.status || 'PENDING';
      case 'letter':
        return certification.letter?.status || 'PENDING';
    }
  };

  const processCredential = async (type: CredentialType, action: Action, reason?: string) => {
    const identifier = certification.email || certification.username;

    switch (action) {
      case 'APPROVE':
          if (type === 'certificate') {
            await certificationsApi.approveByIdentifier(identifier, { status: 'APPROVED', rejectionReason: reason });
          } else if (type === 'badge') {
            await certificationsApi.approveBadgeByIdentifier(identifier, { status: 'APPROVED', rejectionReason: reason });
          } else if (type === 'letter') {
            await certificationsApi.approveLetterByIdentifier(identifier, { status: 'APPROVED', rejectionReason: reason });
          }
          break;
        case 'REJECT':
          if (type === 'certificate') {
            await certificationsApi.approveByIdentifier(identifier, { status: 'REJECTED', rejectionReason: reason });
          } else if (type === 'badge') {
            await certificationsApi.approveBadgeByIdentifier(identifier, { status: 'REJECTED', rejectionReason: reason });
          } else if (type === 'letter') {
            await certificationsApi.approveLetterByIdentifier(identifier, { status: 'REJECTED', rejectionReason: reason });
          }
          break;
        case 'SET_PENDING':
          if (type === 'certificate') {
            await certificationsApi.approveByIdentifier(identifier, { status: 'PENDING' });
          } else if (type === 'badge') {
            await certificationsApi.approveBadgeByIdentifier(identifier, { status: 'PENDING' });
          } else if (type === 'letter') {
            await certificationsApi.approveLetterByIdentifier(identifier, { status: 'PENDING' });
          }
          break;
        case 'ISSUE': {
          let issueBody: IssueCredentialPayload | undefined;
          if (profileIssueOverride) {
            if (profileIssueOverrideReason.trim().length < CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN) {
              throw new Error(
                `Profile override reason must be at least ${CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN} characters`
              );
            }
            issueBody = {
              overrideProfileIncomplete: true,
              overrideReason: profileIssueOverrideReason.trim(),
            };
          }
          if (type === 'certificate') {
            await certificationsApi.issueCertificate(identifier, issueBody);
          } else if (type === 'badge') {
            await certificationsApi.issueBadge(identifier, issueBody);
          } else if (type === 'letter') {
            await certificationsApi.issueLetter(identifier, issueBody);
          }
          break;
        }
    }
  };

  const updateRequirement = async (field: 'theory' | 'clinical' | 'payment' | 'documentation' | 'graduation', value: string) => {
    const identifier = certification.email || certification.username;
    if (!identifier) {
      throw new Error('Invalid certification: missing identifier');
    }

    const statusKeyToBoolean = (field: string, key: string): boolean => {
      if (field === 'theory' || field === 'payment') {
        return key === 'COMPLETED';
      }
      if (field === 'clinical') {
        return key === 'COMPLETED' || key === 'NOT_REQUIRED';
      }
      return key === 'APPROVED';
    };

    const valueBool = statusKeyToBoolean(field, value);
    
    const patch = {
      [field]: { key: value, value: valueBool },
    } as UpdateCertificationPayload;

    await certificationsApi.updateByIdentifier(identifier, patch);
  };

  const handleSubmit = async () => {
    const requirementsChanged = [
      { field: 'theory' as const, value: theoryStatus, current: certification.theory?.key || 'PENDING' },
      { field: 'clinical' as const, value: clinicalStatus, current: certification.clinical?.key || 'NOT_REQUIRED' },
      { field: 'payment' as const, value: paymentStatus, current: certification.payment?.key || 'PENDING' },
      { field: 'documentation' as const, value: documentationStatus, current: certification.documentation?.key || 'PENDING' },
      { field: 'graduation' as const, value: graduationStatus, current: certification.graduation?.key || 'PENDING' },
    ].some((r) => r.value !== r.current);

    if (selectedCredentials.size === 0 && !programNameChanged && !requirementsChanged) {
      toast.error('Please change a requirement, certificate program, or select a credential to process');
      return;
    }

    setIsProcessing(true);
    const identifier = certification.email || certification.username;
    let requirementSuccessCount = 0;
    let requirementFailCount = 0;
    let credentialSuccessCount = 0;
    let credentialFailCount = 0;

    // Step 0: Persist certificate program override (empty → PENDING = use enrollment)
    if (programNameChanged) {
      try {
        toast.loading('Updating certificate program...', { id: 'update-programName' });
        await certificationsApi.updateByIdentifier(identifier, {
          programName: programName.trim() || 'PENDING',
        });
        toast.success('Certificate program updated', { id: 'update-programName' });
        requirementSuccessCount++;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to update certificate program';
        toast.error(errorMsg, { id: 'update-programName' });
        requirementFailCount++;
      }
    }

    // Step 1: Update all requirements
    const requirements = [
      { field: 'theory' as const, value: theoryStatus, label: 'Theory' },
      { field: 'clinical' as const, value: clinicalStatus, label: 'Clinical' },
      { field: 'payment' as const, value: paymentStatus, label: 'Payment' },
      { field: 'documentation' as const, value: documentationStatus, label: 'Documentation' },
      { field: 'graduation' as const, value: graduationStatus, label: 'Graduation' },
    ];

    for (const req of requirements) {
      const currentValue = certification[req.field]?.key || (req.field === 'clinical' ? 'NOT_REQUIRED' : 'PENDING');
      if (req.value !== currentValue) {
        try {
          toast.loading(`Updating ${req.label}...`, { id: `update-${req.field}` });
          await updateRequirement(req.field, req.value);
          toast.success(`${req.label} updated successfully`, { id: `update-${req.field}` });
          requirementSuccessCount++;
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : `Failed to update ${req.label}`;
          toast.error(errorMsg, { id: `update-${req.field}` });
          requirementFailCount++;
        }
      }
    }

    // Step 2: Process selected credentials with their individual statuses
    const credentials = Array.from(selectedCredentials);
    
    for (const credential of credentials) {
      const credentialName = credential.charAt(0).toUpperCase() + credential.slice(1);
      let targetStatus: string;
      
      switch (credential) {
        case 'certificate':
          targetStatus = certificateStatus;
          break;
        case 'badge':
          targetStatus = badgeStatus;
          break;
        case 'letter':
          targetStatus = letterStatus;
          break;
      }

      const currentStatus = getCredentialStatus(credential);
      
      if (targetStatus === currentStatus) {
        continue; // Skip if status hasn't changed
      }

      try {
        toast.loading(`Processing ${credentialName}...`, { id: `process-${credential}` });
        
        if (targetStatus === 'ISSUED') {
          await processCredential(credential, 'ISSUE');
        } else if (targetStatus === 'APPROVED') {
          await processCredential(credential, 'APPROVE');
        } else if (targetStatus === 'REJECTED') {
          if (!rejectionReason.trim()) {
            toast.error(`Please provide a rejection reason for ${credentialName}`, { id: `process-${credential}` });
            credentialFailCount++;
            continue;
          }
          await processCredential(credential, 'REJECT', rejectionReason);
        } else if (targetStatus === 'PENDING') {
          await processCredential(credential, 'SET_PENDING');
        }
        
        toast.success(`${credentialName} updated to ${targetStatus}`, { id: `process-${credential}` });
        credentialSuccessCount++;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : `Failed to process ${credentialName}`;
        toast.error(errorMsg, { id: `process-${credential}` });
        credentialFailCount++;
      }
    }

    setIsProcessing(false);

    // Send achievement email if checkbox is checked and processing was successful
    if (credentialSuccessCount > 0 && sendEmail) {
      try {
        toast.loading('Sending achievement email...', { id: 'send-email' });
        await certificationsApi.sendAchievementEmail(identifier);
        toast.success('Achievement email sent successfully!', { id: 'send-email' });
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to send achievement email';
        toast.error(errorMsg, { id: 'send-email' });
      }
    }

    if (requirementSuccessCount > 0 || credentialSuccessCount > 0) {
      onSuccess();
      if (requirementFailCount === 0 && credentialFailCount === 0) {
        onOpenChange(false);
      }
    }
  };

  const canSubmit =
    selectedCredentials.size > 0 ||
    programNameChanged ||
    theoryStatus !== (certification.theory?.key || 'PENDING') ||
    clinicalStatus !== (certification.clinical?.key || 'NOT_REQUIRED') ||
    paymentStatus !== (certification.payment?.key || 'PENDING') ||
    documentationStatus !== (certification.documentation?.key || 'PENDING') ||
    graduationStatus !== (certification.graduation?.key || 'PENDING');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Process Certification</DialogTitle>
          <DialogDescription>
            Review and process certification credentials for <strong>{certification.username}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Student Information */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Student</Label>
              <p className="font-medium">{certification.username}</p>
              <p className="text-sm text-muted-foreground">{certification.email}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Current enrollment</Label>
              <p className="font-medium">{enrolledProgram || "—"}</p>
              <p className="text-sm text-muted-foreground">{certification.mode}</p>
            </div>
          </div>

          {/* Certificate program (PDF) — independent of current enrollment */}
          <div className="space-y-2 rounded-lg border border-border p-3">
            <Label htmlFor="certificate-program" className="text-sm font-medium">
              Certificate program (printed on PDF)
            </Label>
            <p className="text-xs text-muted-foreground">
              Leave blank to use the student&apos;s current enrollment
              {enrolledProgram ? ` (${enrolledProgram})` : ""}. Set an explicit
              name for historical / Wix programs without changing their enrolled program.
            </p>
            <Input
              id="certificate-program"
              value={programName}
              onChange={(e) => setProgramName(e.target.value)}
              placeholder={enrolledProgram || "e.g. Medical Assistant"}
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground">
              Will print as: <span className="font-medium text-foreground">{printedProgramPreview}</span>
            </p>
          </div>

          {/* Progress Overview */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Overall Progress</Label>
              <Badge variant="outline">{progress}%</Badge>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Requirements Status */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Set Requirements</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Theory</Label>
                <Select value={theoryStatus} onValueChange={setTheoryStatus} disabled={isProcessing}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                    <SelectItem value="PENDING">PENDING</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Clinical</Label>
                <Select value={clinicalStatus} onValueChange={setClinicalStatus} disabled={isProcessing}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                    <SelectItem value="PENDING">PENDING</SelectItem>
                    <SelectItem value="NOT_REQUIRED">NOT_REQUIRED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Payment</Label>
                <Select value={paymentStatus} onValueChange={setPaymentStatus} disabled={isProcessing}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                    <SelectItem value="PENDING">PENDING</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Documentation</Label>
                <Select value={documentationStatus} onValueChange={setDocumentationStatus} disabled={isProcessing}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="APPROVED">APPROVED</SelectItem>
                    <SelectItem value="PENDING">PENDING</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Graduation</Label>
                <Select value={graduationStatus} onValueChange={setGraduationStatus} disabled={isProcessing}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="APPROVED">APPROVED</SelectItem>
                    <SelectItem value="PENDING">PENDING</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Select Credentials */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Set Credential Status & Select to Process</Label>
            <div className="space-y-3">
              {(['certificate', 'badge', 'letter'] as CredentialType[]).map((type) => {
                const currentStatus = getCredentialStatus(type);
                const statusValue = type === 'certificate' ? certificateStatus : type === 'badge' ? badgeStatus : letterStatus;
                const setStatus = type === 'certificate' ? setCertificateStatus : type === 'badge' ? setBadgeStatus : setLetterStatus;
                
                return (
                  <div key={type} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Checkbox
                      id={`credential-${type}`}
                      checked={selectedCredentials.has(type)}
                      onCheckedChange={() => toggleCredential(type)}
                      disabled={isProcessing}
                    />
                    <div className="flex-1 flex items-center gap-3">
                      <span className="font-medium capitalize w-20">{type}</span>
                      <Select 
                        value={statusValue} 
                        onValueChange={(v) => {
                          setStatus(v);
                          // Auto-select credential when status changes
                          if (!selectedCredentials.has(type)) {
                            setSelectedCredentials(prev => new Set(prev).add(type));
                          }
                        }}
                        disabled={isProcessing}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PENDING">PENDING</SelectItem>
                          <SelectItem value="APPROVED">APPROVED</SelectItem>
                          <SelectItem value="REJECTED">REJECTED</SelectItem>
                          <SelectItem value="ISSUED">ISSUED</SelectItem>
                        </SelectContent>
                      </Select>
                      <Badge 
                        variant={getCertificateStatusVariant(currentStatus)}
                        className={getCertificateStatusBadgeClass(currentStatus)}
                      >
                        Current: {currentStatus}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rejection Reason */}
          {Array.from(selectedCredentials).some(type => {
            const status = type === 'certificate' ? certificateStatus : type === 'badge' ? badgeStatus : letterStatus;
            return status === 'REJECTED';
          }) && (
            <div className="space-y-2">
              <Label htmlFor="rejection-reason" className="text-sm">
                Reason for Rejection *
              </Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why the credential(s) are being rejected..."
                rows={3}
                required
              />
            </div>
          )}

          {/* Optional audit override when issuing while student portal profile is incomplete */}
          <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
            <Label className="text-sm font-medium">Profile incomplete override (audit)</Label>
            <p className="text-xs text-muted-foreground">
              Use only when issuing is rejected because the student has not finished Profile & documents.
              Reason is stored on the certification record (min {CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN} characters).
            </p>
            <div className="flex items-center gap-2">
              <Checkbox
                id="profile-issue-override"
                checked={profileIssueOverride}
                onCheckedChange={(c) => setProfileIssueOverride(c === true)}
              />
              <label htmlFor="profile-issue-override" className="cursor-pointer text-sm">
                Send override with issue request
              </label>
            </div>
            {profileIssueOverride && (
              <Textarea
                value={profileIssueOverrideReason}
                onChange={(e) => setProfileIssueOverrideReason(e.target.value)}
                placeholder="Audit reason (required when checked)…"
                rows={3}
              />
            )}
          </div>

          {/* Send Achievement Email */}
          <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
            <Checkbox
              id="send-email"
              checked={sendEmail}
              onCheckedChange={(checked) => setSendEmail(checked as boolean)}
            />
            <label
              htmlFor="send-email"
              className="flex-1 cursor-pointer text-sm"
            >
              <span className="font-medium">Send Achievement Email</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Notify the student about their certification achievement via email
              </p>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isProcessing || 
              !canSubmit ||
              (Array.from(selectedCredentials).some(type => {
                const status = type === 'certificate' ? certificateStatus : type === 'badge' ? badgeStatus : letterStatus;
                return status === 'REJECTED' && !rejectionReason.trim();
              })) ||
              (profileIssueOverride && profileIssueOverrideReason.trim().length < CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN)
            }
          >
            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isProcessing
              ? 'Processing...'
              : selectedCredentials.size > 0
                ? `Process (${selectedCredentials.size} credential${selectedCredentials.size !== 1 ? 's' : ''})`
                : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
